import React, { useContext, useEffect, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import PerformanceWaveChart from "./Performance";
import { LanguageContext } from "../../Languages/LanguageContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrderchart } from "../../../features/dashboard/newBuilderSlice";

const Chart = () => {
  const { t } = useContext(LanguageContext);
  const dispatch = useDispatch();
  const orderChart = useSelector((state) => state.dashboard?.orderChart?.data);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const allMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [performanceData, setPerformanceData] = useState([]);

  // Fetch chart data whenever year or month changes
  useEffect(() => {
    if (selectedYear) {
      const params = { year: selectedYear };
      if (selectedMonth) params.month = selectedMonth;
      dispatch(fetchOrderchart(params));
    }
  }, [selectedYear, selectedMonth, dispatch]);

  // Process API response into chart-ready data
  useEffect(() => {
    if (orderChart?.data) {
      // Wrap single object into array if needed
      const dataArray = Array.isArray(orderChart.data)
        ? orderChart.data
        : [orderChart.data];

      // Map API data to month names
      const mappedData = dataArray.map((item) => ({
        month: item.month,
        name: item.month_name,
        total_orders: item.total_orders,
        completed_orders: item.completed_orders,
      }));

      // Ensure all months are shown on X-axis
      const finalData = allMonths.map((month, index) => {
        const monthObj = mappedData.find((d) => d.name === month);
        if (
          selectedMonth &&
          String(index + 1).padStart(2, "0") !== selectedMonth
        ) {
          // If a specific month is selected, zero out others
          return {
            name: month,
            total_orders: 0,
            completed_orders: 0,
          };
        }
        return (
          monthObj || { name: month, total_orders: 0, completed_orders: 0 }
        );
      });

      setPerformanceData(finalData);
    }
  }, [orderChart, selectedMonth]);

  return (
    <div className="container py-4">
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              {/* Filters */}
              <div className="d-sm-flex  justify-content-between align-items-center mb-3">
                <h5 className="card-title text-light mb-sm-0 mb-2">
                  {t.completed}
                </h5>
                <div className="d-flex justify-content-end  align-items-center mb-3">
                  <select
                    className="form-select form-select-sm me-2  "
                    style={{ width: "100px" }}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-select form-select-sm"
                    style={{ width: "120px" }}
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    disabled={!selectedYear}
                  >
                    <option value="">All Months</option>
                    {allMonths.map((month, i) => (
                      <option
                        key={month}
                        value={String(i + 1).padStart(2, "0")}
                      >
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Chart */}
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="total_orders"
                    fill="#2c3a46"
                    barSize={15}
                    radius={[5, 5, 0, 0]}
                    name="Total"
                  />
                  <Line
                    type="monotone"
                    dataKey="completed_orders"
                    stroke="#3f5263"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Completed"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="shadow-sm h-100">
            <PerformanceWaveChart />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chart;
